import { PI_2, clamp, deg2rad } from '../constants';

export type MotionSample = (roll: number, pitch: number) => void;
export type MotionMode = 'device' | 'mouse' | 'denied' | 'idle';

export interface MotionDiagnostics {
  mode: MotionMode;
  orientationCount: number;
  orientationLastMs: number; // performance.now() of last event, 0 if never
  motionCount: number;
  motionLastMs: number;
  pointerCount: number;
  rawBeta: number;
  rawGamma: number;
  rawAccelX: number;
  rawAccelY: number;
  rawAccelZ: number;
  screenAngle: number;
  hasOrientationApi: boolean;
  hasMotionApi: boolean;
  orientationGranted: boolean;
  motionGranted: boolean;
}

function getScreenAngle(): number {
  const so = (screen as Screen & { orientation?: ScreenOrientation }).orientation;
  if (so && typeof so.angle === 'number') return so.angle;
  const legacy = (window as Window & { orientation?: number }).orientation;
  if (typeof legacy === 'number') return ((legacy % 360) + 360) % 360;
  return 0;
}

interface DOEStatic {
  requestPermission?: () => Promise<'granted' | 'denied' | 'default'>;
}
interface DMEStatic {
  requestPermission?: () => Promise<'granted' | 'denied' | 'default'>;
}

// If deviceorientation hasn't fired in this many ms, derive tilt from devicemotion
// accelerometer instead. iOS sometimes delivers one stream but not the other.
const ORIENTATION_STALE_MS = 500;

export class MotionSource {
  private mode: MotionMode = 'idle';
  private orientationHandler: ((e: DeviceOrientationEvent) => void) | null = null;
  private motionHandler: ((e: DeviceMotionEvent) => void) | null = null;
  private mouseHandler: ((e: MouseEvent) => void) | null = null;
  private touchHandler: ((e: TouchEvent) => void) | null = null;
  private visibilityHandler: (() => void) | null = null;

  private diag: MotionDiagnostics = {
    mode: 'idle',
    orientationCount: 0,
    orientationLastMs: 0,
    motionCount: 0,
    motionLastMs: 0,
    pointerCount: 0,
    rawBeta: 0,
    rawGamma: 0,
    rawAccelX: 0,
    rawAccelY: 0,
    rawAccelZ: 0,
    screenAngle: 0,
    hasOrientationApi: false,
    hasMotionApi: false,
    orientationGranted: false,
    motionGranted: false,
  };

  constructor(private readonly onSample: MotionSample) {}

  getMode(): MotionMode {
    return this.mode;
  }

  getEventCount(): number {
    return this.diag.orientationCount + this.diag.motionCount;
  }

  getDiagnostics(): Readonly<MotionDiagnostics> {
    return this.diag;
  }

  /**
   * Must be invoked from a user gesture. CRITICAL: callers must NOT `await`
   * anything before calling this — iOS Safari requires
   * `DeviceOrientationEvent.requestPermission()` to be dispatched synchronously
   * from the gesture, and any prior `await` consumes the activation.
   */
  async start(): Promise<MotionMode> {
    const DOE = (window as unknown as { DeviceOrientationEvent?: DOEStatic }).DeviceOrientationEvent;
    const DME = (window as unknown as { DeviceMotionEvent?: DMEStatic }).DeviceMotionEvent;
    this.diag.hasOrientationApi = !!DOE;
    this.diag.hasMotionApi = !!DME;

    const doeNeedsPerm = !!(DOE && typeof DOE.requestPermission === 'function');
    const dmeNeedsPerm = !!(DME && typeof DME.requestPermission === 'function');

    if (doeNeedsPerm || dmeNeedsPerm) {
      // iOS path. Dispatch both permission calls synchronously, in parallel, so
      // a single user gesture covers both sensor APIs.
      const doePromise = doeNeedsPerm
        ? DOE!.requestPermission!().catch(() => 'denied' as const)
        : Promise.resolve('granted' as const);
      const dmePromise = dmeNeedsPerm
        ? DME!.requestPermission!().catch(() => 'denied' as const)
        : Promise.resolve('granted' as const);
      const [doeRes, dmeRes] = await Promise.all([doePromise, dmePromise]);
      this.diag.orientationGranted = doeRes === 'granted';
      this.diag.motionGranted = dmeRes === 'granted';

      if (this.diag.orientationGranted) this.attachOrientation();
      if (this.diag.motionGranted) this.attachMotion();
      if (this.diag.orientationGranted || this.diag.motionGranted) {
        this.attachVisibilityRecovery();
        return this.setMode('device');
      }
      // Both denied. Fall back to pointer/touch on iOS so the UI isn't frozen.
      this.attachPointer();
      return this.setMode('denied');
    }

    // Non-iOS path. Attach what's available without a permission gate.
    let attached = false;
    if (DOE) {
      this.attachOrientation();
      this.diag.orientationGranted = true;
      attached = true;
    }
    if (DME) {
      this.attachMotion();
      this.diag.motionGranted = true;
      attached = true;
    }
    if (attached) {
      this.attachVisibilityRecovery();
      return this.setMode('device');
    }
    this.attachPointer();
    return this.setMode('mouse');
  }

  stop(): void {
    if (this.orientationHandler) {
      window.removeEventListener('deviceorientation', this.orientationHandler);
    }
    if (this.motionHandler) {
      window.removeEventListener('devicemotion', this.motionHandler);
    }
    if (this.mouseHandler) {
      window.removeEventListener('mousemove', this.mouseHandler);
    }
    if (this.touchHandler) {
      window.removeEventListener('touchmove', this.touchHandler as EventListener);
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      window.removeEventListener('pageshow', this.visibilityHandler);
    }
    this.orientationHandler = null;
    this.motionHandler = null;
    this.mouseHandler = null;
    this.touchHandler = null;
    this.visibilityHandler = null;
    this.setMode('idle');
  }

  /**
   * Reset to idle and re-run `start()`. Must be called from a user gesture
   * for the iOS permission re-prompt to appear.
   */
  async retry(): Promise<MotionMode> {
    this.stop();
    return this.start();
  }

  /**
   * Detach and re-attach the sensor listeners. iOS WebKit sometimes silently
   * stops delivering events to a registered listener — cycling the listener
   * appears to wake it back up. Safe to call from any user interaction.
   */
  refresh(): void {
    if (this.orientationHandler) {
      const h = this.orientationHandler;
      window.removeEventListener('deviceorientation', h);
      window.addEventListener('deviceorientation', h);
    }
    if (this.motionHandler) {
      const h = this.motionHandler;
      window.removeEventListener('devicemotion', h);
      window.addEventListener('devicemotion', h);
    }
  }

  private setMode(m: MotionMode): MotionMode {
    this.mode = m;
    this.diag.mode = m;
    return m;
  }

  private remap(betaDeg: number, gammaDeg: number): { pitch: number; roll: number } {
    // beta = rotation around X (front/back), gamma = around Y (left/right) — both
    // in the device's natural (portrait) frame. Remap into the user's perceived
    // landscape/portrait frame so "tilt forward" is always pitch and "tilt
    // left/right" is always roll.
    const angle = getScreenAngle();
    this.diag.screenAngle = angle;
    let userPitchDeg: number;
    let userRollDeg: number;
    switch (angle) {
      case 90:
        userPitchDeg = -gammaDeg;
        userRollDeg = betaDeg;
        break;
      case -90:
      case 270:
        userPitchDeg = gammaDeg;
        userRollDeg = -betaDeg;
        break;
      case 180:
        userPitchDeg = -betaDeg;
        userRollDeg = -gammaDeg;
        break;
      case 0:
      default:
        userPitchDeg = betaDeg;
        userRollDeg = gammaDeg;
        break;
    }
    return {
      pitch: clamp(deg2rad(userPitchDeg), -PI_2, PI_2),
      roll: clamp(deg2rad(userRollDeg), -PI_2, PI_2),
    };
  }

  private attachOrientation(): void {
    this.orientationHandler = (e: DeviceOrientationEvent) => {
      this.diag.orientationCount++;
      this.diag.orientationLastMs = performance.now();
      const beta = e.beta ?? 0;
      const gamma = e.gamma ?? 0;
      this.diag.rawBeta = beta;
      this.diag.rawGamma = gamma;
      const { pitch, roll } = this.remap(beta, gamma);
      this.onSample(roll, pitch);
    };
    window.addEventListener('deviceorientation', this.orientationHandler);
  }

  private attachMotion(): void {
    this.motionHandler = (e: DeviceMotionEvent) => {
      this.diag.motionCount++;
      this.diag.motionLastMs = performance.now();
      const a = e.accelerationIncludingGravity;
      if (!a) return;
      const ax = a.x ?? 0;
      const ay = a.y ?? 0;
      const az = a.z ?? 0;
      this.diag.rawAccelX = ax;
      this.diag.rawAccelY = ay;
      this.diag.rawAccelZ = az;
      // If deviceorientation is firing freshly, prefer it; this stream only
      // exists as a fallback for when iOS stalls the orientation events.
      const orientationFresh =
        this.diag.orientationLastMs > 0 &&
        performance.now() - this.diag.orientationLastMs < ORIENTATION_STALE_MS;
      if (orientationFresh) return;
      // Derive tilt from the gravity vector. With the device flat on its back,
      // gravity is ~(0, 0, +9.81). beta is rotation around X (forward tilt),
      // gamma is rotation around Y (side tilt).
      const betaDeg = (Math.atan2(-ay, az) * 180) / Math.PI;
      const gammaDeg = (Math.atan2(ax, Math.sqrt(ay * ay + az * az)) * 180) / Math.PI;
      this.diag.rawBeta = betaDeg;
      this.diag.rawGamma = gammaDeg;
      const { pitch, roll } = this.remap(betaDeg, gammaDeg);
      this.onSample(roll, pitch);
    };
    window.addEventListener('devicemotion', this.motionHandler);
  }

  private attachPointer(): void {
    const sample = (clientX: number, clientY: number): void => {
      this.diag.pointerCount++;
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      const roll = (clientX / w - 0.5) * Math.PI;
      const pitch = (clientY / h - 0.5) * Math.PI;
      this.onSample(clamp(roll, -PI_2, PI_2), clamp(pitch, -PI_2, PI_2));
    };
    this.mouseHandler = (e: MouseEvent) => sample(e.clientX, e.clientY);
    this.touchHandler = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) sample(t.clientX, t.clientY);
    };
    window.addEventListener('mousemove', this.mouseHandler);
    window.addEventListener('touchmove', this.touchHandler as EventListener, { passive: true });
  }

  private attachVisibilityRecovery(): void {
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.refresh();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
    window.addEventListener('pageshow', this.visibilityHandler);
  }
}
